import { ChatAuthService } from './../services/chat-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ChatLoggingService } from '../services/chat-logging.service';
import { HttpClient } from '@angular/common/http';
import { ActionCardBehaviorActions, ChatAnalyticAttributes } from '../classes/constants';
import { AnalyticObject } from '../interfaces/analytics-interfaces';
import type { ChatItemComponent } from '../components/chat-item/chat-item.component';
import type { ChatComponent } from '../components/chat/chat.component';
import { default as AnalyticEvents } from '@mitel-internal/cloudlink-analytic-events/analytic-events.json';

export class ActionCardHelpers {
  static postWebhookPayload(
    $event: Event,
    url: string,
    payload: any,
    title: string,
    behavior: any,
    retry401: boolean = true,
    chatAuthService: ChatAuthService,
    chatLoggingService: ChatLoggingService,
    httpClient: HttpClient,
    translateService: TranslateService,
    componentName: string,
    chatComponent: ChatComponent,
    chatItemComponent: ChatItemComponent
  ) {
    if ($event) {
      $event.preventDefault;
    }
    return this.sendWebhookPayload(
      'POST',
      url,
      payload,
      title,
      behavior,
      retry401,
      chatAuthService,
      chatLoggingService,
      httpClient,
      translateService,
      componentName,
      chatComponent,
      chatItemComponent
    );
  }

  static async sendWebhookPayload(
    method: 'PUT' | 'POST' | 'DELETE' | 'PATCH',
    url: string,
    payload: any,
    title: string,
    behavior: any,
    retry401: boolean = true,
    chatAuthService: ChatAuthService,
    chatLoggingService: ChatLoggingService,
    httpClient: HttpClient,
    translateService: TranslateService,
    componentName: string,
    chatComponent: ChatComponent,
    chatItemComponent: ChatItemComponent
  ) {
    // Standard header
    const requestoptions: any = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    // Check if this is a mitel.io webhook. issue standard access token in headers
    var anchorElement = document.createElement('a');
    anchorElement.href = url;
    if (anchorElement.hostname.endsWith('mitel.io')) {
      requestoptions.headers['Authorization'] = chatAuthService.token.access_token;
    }

    // Invoke
    try {
      if (payload) {
        requestoptions.body = payload;
      }
      let analyticObj: AnalyticObject = {
        eventName: AnalyticEvents.analytic_events.common.event_name.chat.send_action_card_webhook,
        attributes: ChatAnalyticAttributes
      };
      chatComponent.onAnalyticEvent.emit(analyticObj);
      await httpClient.request(method, url, requestoptions).toPromise();
      chatLoggingService.info({
        logMessage: componentName + '.sendWebhookPayload() success'
      });
      this.actionCardBehavior(behavior, true, chatComponent, chatItemComponent);
    } catch (ex) {
      if (ex.status == 401 && retry401 && anchorElement.hostname.endsWith('mitel.io')) {
        chatLoggingService.error({
          logMessage: componentName + '.sendWebhookPayload(): Error 401 mitel.io (attempt refresh)'
        });
        await chatAuthService.refreshToken();
        this.postWebhookPayload(
          null,
          url,
          payload,
          title,
          behavior,
          false,
          chatAuthService,
          chatLoggingService,
          httpClient,
          translateService,
          componentName,
          chatComponent,
          chatItemComponent
        );
      } else {
        chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: componentName + '.sendWebhookPayload() failure',
            logParameters: [url, ex]
          },
          errorMessage: translateService.instant('cl_chat.failedToPostWebhook')
        });
        this.actionCardBehavior(behavior, false, chatComponent, chatItemComponent);
      }
    }
  }

  static openLink(
    $event: Event,
    url: string,
    title: string,
    behavior: any,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    chatComponent: ChatComponent,
    chatItemComponent: ChatItemComponent
  ) {
    if ($event) {
      $event.preventDefault;
    }
    let event = new CustomEvent('action-card-open-link', {
      bubbles: false,
      cancelable: true,
      detail: {
        url: url
      }
    });
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.open_action_card_link,
      attributes: ChatAnalyticAttributes
    };
    chatComponent.onAnalyticEvent.emit(analyticObj);
    chatComponent.onActionCardOpenLink.emit(event);
    if (!event.defaultPrevented) {
      window.open(url, '_blank');
      chatComponent.onWindowOpen.emit(url);
    }
    this.actionCardBehavior(behavior, true, chatComponent, chatItemComponent);
    chatLoggingService.info({
      logMessage: componentName + '.openLink() success'
    });
  }

  static async actionCardBehavior(
    behavior: any,
    success: boolean,
    chatComponent: ChatComponent,
    chatItemComponent: ChatItemComponent
  ) {
    if (behavior) {
      if (success) {
        if (behavior.success) {
          this.handleBehaviorResponse(behavior.success, chatComponent, chatItemComponent);
        } else {
          //Deprecated behavior
          this.handleBehaviorResponse(behavior, chatComponent, chatItemComponent);
        }
      } else {
        if (behavior.failure) {
          this.handleBehaviorResponse(behavior.failure, chatComponent, chatItemComponent);
        }
      }
    }
  }

  static handleBehaviorResponse(response, chatComponent: ChatComponent, chatItemComponent: ChatItemComponent) {
    if (response.action === ActionCardBehaviorActions.POSTMESSAGE && response.payload) {
      chatComponent.send(response.payload);
    } else if (response.action === ActionCardBehaviorActions.POSTREACTION && response.payload) {
      if (!chatItemComponent.myReactions[response.payload]) {
        chatItemComponent.setReaction(response.payload);
      }
    }
  }
}

export interface OpenLinkEvent {
  $event: Event;
  url: string;
  title: string;
  behavior: any;
  componentName: string;
  chatItem?: ChatItemComponent;
}

export interface SendWebhookPayloadEvent {
  method: 'PUT' | 'POST' | 'DELETE' | 'PATCH';
  url: string;
  payload: any;
  title: string;
  behavior: any;
  retry401: boolean;
  componentName: string;
  chatItem?: ChatItemComponent;
}

export interface PostWebhookPayloadEvent {
  $event: Event;
  url: string;
  payload: any;
  title: string;
  behavior: any;
  retry401: boolean;
  componentName: string;
  chatItem?: ChatItemComponent;
}

export interface ActionCardBehaviorEvent {
  behavior: any;
  success: boolean;
  chatItem?: ChatItemComponent;
}

export interface ActionCardMessageEvent {
  message: string;
  fallbackText?: string;
  throwError?: boolean;
}

export interface GetCurrentLocationEvent {
  throwError: boolean;
  sendLocation: boolean;
}
